import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { toast } from 'react-toastify';

const ContactMessagesTable = () => {
    const { auth } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/contact`);
            if (response.ok) {
                const data = await response.json();
                setMessages(data);
            } else {
                toast.error('Failed to fetch messages');
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Error loading messages');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/contact/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                toast.success(`Message marked as ${newStatus}`);
                // Optimistic update
                setMessages(prev => prev.map(msg => 
                    msg.id === id ? { ...msg, status: newStatus } : msg
                ));
            } else {
                toast.error('Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Error updating status');
        }
    };

    const filteredMessages = messages.filter(msg => {
        if (filterStatus === 'all') return true;
        return msg.status === filterStatus;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'unread':
                return <span className="badge bg-danger-100 text-danger-600 px-3 py-2 rounded-pill fw-medium">Unread</span>;
            case 'read':
                return <span className="badge bg-success-100 text-success-600 px-3 py-2 rounded-pill fw-medium">Read</span>;
            case 'archived':
                return <span className="badge bg-secondary-100 text-secondary-600 px-3 py-2 rounded-pill fw-medium">Archived</span>;
            default:
                return <span className="badge bg-secondary-100 text-secondary-600 px-3 py-2 rounded-pill fw-medium">{status}</span>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select 
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                        <option value="archived">Archived</option>
                    </select>
                </div>
                <button 
                    className="btn btn-primary-600 d-flex align-items-center gap-2"
                    onClick={fetchMessages}
                >
                    <Icon icon="mdi:refresh" className="text-xl" />
                    Refresh
                </button>
            </div>
            
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table hover-table">
                        <thead>
                            <tr>
                                <th scope="col">Status</th>
                                <th scope="col">Date</th>
                                <th scope="col">Name</th>
                                <th scope="col">Contact Info</th>
                                <th scope="col">Message</th>
                                <th scope="col" className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredMessages.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <Icon icon="mdi:inbox-outline" className="text-6xl text-secondary-light mb-3 opacity-50" />
                                        <p className="text-secondary-light">No messages found</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredMessages.map((msg) => (
                                    <tr key={msg.id}>
                                        <td>{getStatusBadge(msg.status)}</td>
                                        <td>{formatDate(msg.created_at)}</td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="fw-medium text-primary-600">{msg.name}</span>
                                                {!msg.is_guest && (
                                                    <span className="text-xs text-info-600 badge bg-info-50 w-auto d-inline-block">User ({msg.user_role})</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                <div className="d-flex align-items-center gap-1 text-sm">
                                                    <Icon icon="mdi:email" className="text-secondary-light" />
                                                    {msg.email}
                                                </div>
                                                <div className="d-flex align-items-center gap-1 text-sm">
                                                    <Icon icon="mdi:phone" className="text-secondary-light" />
                                                    {msg.phone}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="text-secondary-light" style={{ maxWidth: '400px', whiteSpace: 'pre-wrap' }}>
                                                {msg.message}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                {msg.status !== 'read' && (
                                                    <button 
                                                        className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handleStatusUpdate(msg.id, 'read')}
                                                        title="Mark as Read"
                                                    >
                                                        <Icon icon="mdi:check" />
                                                    </button>
                                                )}
                                                {msg.status !== 'unread' && (
                                                    <button 
                                                        className="w-32-px h-32-px bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handleStatusUpdate(msg.id, 'unread')}
                                                        title="Mark as Unread"
                                                    >
                                                        <Icon icon="mdi:email-outline" />
                                                    </button>
                                                )}
                                                {msg.status !== 'archived' && (
                                                    <button 
                                                        className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handleStatusUpdate(msg.id, 'archived')}
                                                        title="Archive"
                                                    >
                                                        <Icon icon="mdi:archive-arrow-down" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ContactMessagesTable;
